import { useRef, useState } from "react";
import { Printer, Tag, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { fmtNum, uuid } from "@/lib/format";
import { makeScaleBarcode } from "@/lib/weigh";
import { BigButton, Chip, Empty } from "@/components/ui-kit";
import { Label } from "@/components/inventory/LabelsDialog";
import WeighPanel from "@/components/scale/WeighPanel";
import { printElement } from "@/lib/print";

export default function WeighLabelDialog({ open, onClose, products }) {
  const { t, view } = useApp();
  const sheet = useRef(null);
  const weighed = products.filter((p) => p.sold_by_weight && p.plu);
  const [pid, setPid] = useState(null);
  const [labels, setLabels] = useState([]);
  const product = weighed.find((p) => p.id === pid) || weighed[0];
  const create = (kg, total) => {
    const barcode = makeScaleBarcode(product, kg, total, view.shop);
    if (!barcode) return toast.error(t("scale_label_too_big"));
    setLabels((l) => [...l, { key: uuid(), name: product.name, barcode, price: total, extra: `${kg.toFixed(3)} kg · ${fmtNum(product.price)}/kg` }]);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[94vh] overflow-y-auto" data-testid="weigh-label-dialog">
        <DialogTitle className="font-heading text-2xl">{t("weigh_label")}</DialogTitle>
        {!product ? <Empty icon={Tag} text={t("no_weighed_products")} testid="weigh-label-empty" /> : (
          <div className="grid md:grid-cols-2 gap-4 no-print">
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {weighed.map((p) => <Chip key={p.id} active={p.id === product.id} onClick={() => setPid(p.id)} data-testid={`weigh-label-product-${p.id}`}>{p.name}</Chip>)}
              </div>
              <WeighPanel product={product} onConfirm={create} confirmLabel={t("create_label")} confirmIcon={Tag} />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center font-bold">
                <span data-testid="weigh-label-count">{t("labels")}: {labels.length}</span>
                {labels.length > 0 && <button type="button" onClick={() => setLabels([])} className="text-destructive text-sm flex items-center gap-1" data-testid="weigh-label-clear"><Trash2 className="w-4 h-4" />{t("clear")}</button>}
              </div>
              <BigButton disabled={!labels.length} onClick={() => printElement(sheet.current, { title: "etiquetas", brand: false })} className="w-full" data-testid="weigh-label-print-btn"><Printer className="w-5 h-5" />{t("print")} ({labels.length})</BigButton>
            </div>
          </div>
        )}
        <div ref={sheet} className="print-area grid grid-cols-2 sm:grid-cols-3 gap-2" style={{ width: "auto" }}>
          {labels.map((l) => <Label key={l.key} p={l} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
